import { ArrowLeft, X, ArrowRight, Trophy } from 'lucide-react';

const TOP3_PILLAR = [
  { bg: 'bg-mp-lime',     text: 'text-mp-darkgreen', border: 'border-mp-lime',     avatarBg: 'bg-mp-darkgreen text-mp-lime' },
  { bg: 'bg-mp-teal',     text: 'text-white',         border: 'border-mp-teal',     avatarBg: 'bg-white text-mp-teal' },
  { bg: 'bg-mp-bg-mint',  text: 'text-mp-darkgreen', border: 'border-mp-bg-mint',  avatarBg: 'bg-mp-darkgreen text-mp-bg-mint' },
];

const REST_AVATARS = ['bg-mp-bg-mint', 'bg-mp-fox-cream', 'bg-mp-bg-blue', 'bg-mp-bg-pink'];

export default function Leaderboard({ participants, currentRound, totalRounds, onNextQuestion, onBack, onExit, autoAdvanceCountdown }) {
  const sortedParticipants = [...participants].sort((a, b) => (b.score || 0) - (a.score || 0));
  const top3 = sortedParticipants.slice(0, 3);
  const rest = sortedParticipants.slice(3, 5);

  const getInitial = (name) => name?.charAt(0)?.toUpperCase() || '?';

  return (
    <div className="bg-mp-bg-blue font-body min-h-dvh flex flex-col overflow-hidden relative">
      {/* Confetti decorations */}
      <div className="absolute inset-0 pointer-events-none opacity-70">
        <span className="absolute top-[12%] left-[10%] w-3 h-3 bg-mp-magenta rounded-sm rotate-[20deg]" />
        <span className="absolute top-[16%] right-[15%] w-3 h-3 bg-mp-teal rounded-sm -rotate-[12deg]" />
        <span className="absolute top-[28%] left-[40%] w-2 h-2 bg-mp-fox-rust rounded-full" />
        <span className="absolute bottom-[35%] right-[12%] w-3 h-3 bg-mp-lime rounded-sm rotate-[15deg]" />
      </div>

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between p-4 pt-5 max-w-md mx-auto w-full">
        <button onClick={onBack} className="flex items-center justify-center w-10 h-10 rounded-full text-mp-darkgreen hover:bg-mp-darkgreen/5 transition-colors" aria-label="Tilbake">
          <ArrowLeft size={20} />
        </button>
        <div className="flex flex-col items-center">
          <span className="mp-eyebrow">Mellomstilling</span>
          <p className="font-display text-mp-darkgreen text-lg">Runde {currentRound} av {totalRounds}</p>
        </div>
        {onExit ? (
          <button onClick={onExit} className="flex items-center justify-center w-10 h-10 rounded-full text-mp-darkgreen/60 hover:bg-mp-darkgreen/5 hover:text-mp-darkgreen transition-colors" aria-label="Avslutt økt">
            <X size={20} />
          </button>
        ) : (
          <div className="w-10" />
        )}
      </header>

      {/* Main */}
      <main className="relative z-10 flex-1 flex flex-col w-full max-w-md mx-auto px-4 pb-6">
        {/* Podium */}
        <div className="flex items-end justify-center gap-3 pb-6 pt-4 min-h-[300px]">
          {/* 2nd */}
          {top3[1] && (
            <div className="flex flex-col items-center w-1/3">
              <div className={`w-14 h-14 rounded-full ${TOP3_PILLAR[1].avatarBg} flex items-center justify-center font-display text-xl border-4 ${TOP3_PILLAR[1].border} mb-2 shadow-mp-2`}>
                {getInitial(top3[1].name)}
              </div>
              <p className="font-display text-mp-darkgreen text-sm truncate w-full text-center mb-1">{top3[1].name}</p>
              <p className="font-display text-mp-magenta text-sm mb-2">{(top3[1].score || 0).toLocaleString()}</p>
              <div className={`w-full ${TOP3_PILLAR[1].bg} ${TOP3_PILLAR[1].text} rounded-t-2xl h-28 shadow-mp-2 flex flex-col items-center justify-start pt-3`}>
                <span className="font-display text-3xl leading-none">2</span>
                <span className="font-display text-[10px] uppercase tracking-[0.18em] opacity-70 mt-1">poeng</span>
              </div>
            </div>
          )}

          {/* 1st */}
          {top3[0] && (
            <div className="flex flex-col items-center w-1/3 z-20">
              <Trophy size={28} className="text-mp-fox-rust mb-1 animate-bounce" />
              <div className={`w-16 h-16 rounded-full ${TOP3_PILLAR[0].avatarBg} flex items-center justify-center font-display text-2xl border-4 ${TOP3_PILLAR[0].border} mb-2 shadow-mp-3`}>
                {getInitial(top3[0].name)}
              </div>
              <p className="font-display text-mp-darkgreen text-base truncate w-full text-center mb-1">{top3[0].name}</p>
              <p className="font-display text-mp-magenta text-base mb-2">{(top3[0].score || 0).toLocaleString()}</p>
              <div className={`w-full ${TOP3_PILLAR[0].bg} ${TOP3_PILLAR[0].text} rounded-t-2xl h-40 shadow-mp-3 flex flex-col items-center justify-start pt-4`}>
                <span className="font-display text-4xl leading-none">1</span>
                <span className="font-display text-[11px] uppercase tracking-[0.18em] opacity-70 mt-1">vinnar</span>
              </div>
            </div>
          )}

          {/* 3rd */}
          {top3[2] && (
            <div className="flex flex-col items-center w-1/3">
              <div className={`w-14 h-14 rounded-full ${TOP3_PILLAR[2].avatarBg} flex items-center justify-center font-display text-xl border-4 ${TOP3_PILLAR[2].border} mb-2 shadow-mp-2`}>
                {getInitial(top3[2].name)}
              </div>
              <p className="font-display text-mp-darkgreen text-sm truncate w-full text-center mb-1">{top3[2].name}</p>
              <p className="font-display text-mp-magenta text-sm mb-2">{(top3[2].score || 0).toLocaleString()}</p>
              <div className={`w-full ${TOP3_PILLAR[2].bg} ${TOP3_PILLAR[2].text} rounded-t-2xl h-20 shadow-mp-2 flex flex-col items-center justify-start pt-3`}>
                <span className="font-display text-2xl leading-none">3</span>
                <span className="font-display text-[10px] uppercase tracking-[0.18em] opacity-70 mt-1">poeng</span>
              </div>
            </div>
          )}
        </div>

        {/* Rest */}
        {rest.length > 0 && (
          <div className="flex flex-col gap-2.5 w-full mb-4">
            {rest.map((participant, index) => (
              <div
                key={participant.id}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white border border-[rgba(17,73,66,0.10)] shadow-mp-1"
              >
                <span className="flex items-center justify-center w-7 h-7 rounded-full bg-mp-bg-lightgreen font-display text-mp-darkgreen text-sm shrink-0">
                  {index + 4}
                </span>
                <div className={`w-9 h-9 shrink-0 rounded-full ${REST_AVATARS[index % REST_AVATARS.length]} flex items-center justify-center font-display text-mp-darkgreen text-sm`}>
                  {getInitial(participant.name)}
                </div>
                <p className="flex-1 font-display text-mp-darkgreen text-base leading-tight truncate">
                  {participant.name}
                </p>
                <p className="font-display text-mp-magenta text-base">{(participant.score || 0).toLocaleString()}</p>
              </div>
            ))}
          </div>
        )}

        {/* Action */}
        <div className="mt-auto flex flex-col items-center gap-2">
          {autoAdvanceCountdown !== null && autoAdvanceCountdown > 0 && (
            <span className="font-display text-mp-stone-500 text-xs tabular-nums">
              Neste spørsmål om {autoAdvanceCountdown}s
            </span>
          )}
          <button onClick={onNextQuestion} className="mp-cta mp-cta-large w-full">
            <span>Neste spørsmål</span>
            <ArrowRight size={20} />
          </button>
        </div>
      </main>
    </div>
  );
}
